/**
 * SignUpWrapper - Feature flag and experiment wrapper for sign-up page
 *
 * Routing logic (in order of priority):
 * 1. VIP invite codes: Always show sign-up flow to validate VIP access
 * 2. SignUpExperiment='quizlet': Redirect to quizlet flow (A/B test variant)
 * 3. PaymentPageV2 enabled: Redirect to payment page (control behavior)
 * 4. Default: Render legacy sign-up page
 */

import { Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { FeatureFlags, useFeatureFlags, VariantFlags } from '@/services/feature-flag-provider';
import { ROUTE_PAYMENT_COLLECT_PAYMENT_METHOD, ROUTE_QUIZLET_VERSIONED } from '@/constants/routes';
import { getRoute } from '@/utils';
import authService from '@/services/auth.service';
import Page from './sign-up.page';

/**
 * Check if an invite code is a VIP code that requires the sign-up flow
 * VIP codes start with "VIP_" or "VIP-" or end with "_VIP" or "-VIP" (case insensitive)
 */
function isVipInviteCode(code: string | null | undefined): boolean {
  if (!code) return false;
  const lowerCode = code.toLowerCase();
  return (
    lowerCode.startsWith('vip_') ||
    lowerCode.startsWith('vip-') ||
    lowerCode.endsWith('_vip') ||
    lowerCode.endsWith('-vip')
  );
}

export function SignUpWrapper() {
  const { isEnabled, isLoading, getVariant } = useFeatureFlags();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Show blank screen while feature flags are loading to prevent flash
  if (isLoading) {
    return <div className="min-h-screen bg-white" />;
  }

  // Check for VIP invite code from URL or localStorage
  const urlInviteCode = searchParams.get('invite-code');
  const storedInviteCode = authService.getInviteCode();
  const hasVipCode = isVipInviteCode(urlInviteCode) || isVipInviteCode(storedInviteCode);

  const showV2 = isEnabled(FeatureFlags.IsPaymentPageV2Enabled);
  const signUpExperiment = getVariant(VariantFlags.SignUpExperiment);

  // VIP users must go through sign-up flow to validate VIP access
  // and potentially bypass payment entirely
  if (hasVipCode) {
    return <Page />;
  }

  // A/B test: quizlet variant redirects to quizlet flow instead of pricing page
  if (signUpExperiment === 'quizlet') {
    return <Navigate to={`${ROUTE_QUIZLET_VERSIONED}${location.search}`} replace />;
  }

  // Control: existing behavior - redirect to payment page if V2 enabled
  if (showV2) {
    return <Navigate to={`${getRoute(ROUTE_PAYMENT_COLLECT_PAYMENT_METHOD)}${location.search}`} replace />;
  }

  return <Page />;
}

export default SignUpWrapper;
